import { useEffect, useMemo, useRef, useState, type PointerEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";
import {
  api,
  unwrap,
  type TimelineFile,
  type TimelineEvent,
  type TimelineLane,
} from "../api/client";
import { hasPermission, useSession } from "../auth/session";
import { goTo as navigate } from "../nav";
import { byUnit, filterLanes } from "../map/area";
import { MapView, type Area } from "../map/MapView";
import type { Interval } from "../map/tracks";
import { EventsList } from "./EventsList";
import { WordSearch } from "./WordSearch";
import { GpsAnalysis } from "./GpsAnalysis";
import { signedSeconds } from "./ClockPanel";
import { pinched } from "./gestures";
import { COARSE } from "../theme";
import {
  byLane,
  describe,
  isOfInterest,
  withWords,
  laneKey,
  nextEvent,
  previousEvent,
} from "./events";

const HOUR = 3_600_000;
const DAY = 24 * HOUR;
const LEVELS = [1, 4, 10]; // hours: <1, 1–4, 4–10, >10

export interface Range {
  from: number;
  to: number;
}

const iso = (ms: number) => new Date(ms).toISOString();
const utcDay = (ms: number) => Math.floor(ms / DAY) * DAY;

function hhmm(ms: number, withSeconds = false): string {
  return new Date(ms).toISOString().slice(11, withSeconds ? 19 : 16);
}

/** A tick step giving at most `most` ticks across the range. */
function tickStep(span: number, most = 12): number {
  const steps = [
    60_000,
    300_000,
    900_000,
    1_800_000,
    HOUR,
    3 * HOUR,
    6 * HOUR,
    12 * HOUR,
    DAY,
  ];
  return steps.find((s) => span / s <= most) ?? DAY;
}

/** "lib:<id>", "case:<id>" or "" (every library the caller can view), from the URL. */
export function showFromUrl(): string {
  const q = new URLSearchParams(window.location.search);
  const c = q.get("case");
  const l = q.get("library");
  return c ? `case:${c}` : l ? `lib:${l}` : "";
}

function showQuery(show: string): string {
  if (show.startsWith("case:")) return `case=${show.slice(5)}`;
  if (show.startsWith("lib:")) return `library=${show.slice(4)}`;
  return "";
}

/** The Playback page at `at`, showing what the Timeline showed, over `range`. */
export function playbackUrl(at: number, show: string, play = false, range?: Range): string {
  const q = [
    `at=${encodeURIComponent(iso(at))}`,
    showQuery(show),
    play ? "play=1" : "",
    range ? `from=${encodeURIComponent(iso(range.from))}&to=${encodeURIComponent(iso(range.to))}` : "",
  ];
  return `/playback?${q.filter(Boolean).join("&")}`;
}

export function timelineUrl(show: string, at?: number): string {
  const q = [showQuery(show), at === undefined ? "" : `at=${encodeURIComponent(iso(at))}`];
  return `/timeline?${q.filter(Boolean).join("&")}`;
}

export function showFilter(show: string, reviewOnly = false) {
  return {
    library: show.startsWith("lib:") ? show.slice(4) : undefined,
    case: show.startsWith("case:") ? show.slice(5) : undefined,
    review_only: reviewOnly,
  };
}

/** The lanes and events of `shown` for what `show` names. */
export function useLanesAndEvents(
  shown: Range | null,
  show: string,
  reviewOnly = false,
) {
  const filter = showFilter(show, reviewOnly);
  const query = {
    from: iso(shown?.from ?? 0),
    to: iso(shown?.to ?? 1),
    ...filter,
  };
  const timeline = useQuery({
    queryKey: ["timeline", "lanes", shown?.from, shown?.to, show, reviewOnly],
    enabled: shown !== null,
    queryFn: () => unwrap(api.GET("/api/timeline", { params: { query } })),
  });
  const events = useQuery({
    queryKey: ["timeline", "events", shown?.from, shown?.to, show, reviewOnly],
    enabled: shown !== null,
    queryFn: () => unwrap(api.GET("/api/timeline/events", { params: { query } })),
  });
  return { timeline, events };
}

/**
 * Finding recordings (design §7.1): by day, library or case, words said and
 * places on the map. Choosing a time opens it on the Playback page.
 */
export function TimelinePage() {
  const [library, setLibrary] = useState<string>(showFromUrl);
  const [reviewOnly, setReviewOnly] = useState(false);
  const [month, setMonth] = useState(() => {
    const at = Date.parse(
      new URLSearchParams(window.location.search).get("at") ?? "",
    );
    const now = new Date(Number.isNaN(at) ? Date.now() : at);
    return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
  });
  const [range, setRange] = useState<Range | null>(() => {
    const at = Date.parse(
      new URLSearchParams(window.location.search).get("at") ?? "",
    );
    return Number.isNaN(at) ? null : { from: utcDay(at), to: utcDay(at) + DAY };
  });
  const [jump, setJump] = useState("");
  const [brush, setBrush] = useState<Range | null>(null);
  const [area, setArea] = useState<Area | null>(null);
  const [inArea, setInArea] = useState<Map<string, Interval[]> | null>(null);
  const play = (utcMs: number, now = false, around?: Range) =>
    navigate(playbackUrl(utcMs, library, now, around ?? shown ?? undefined));
  const session = useSession();
  const canResolve = !!session.data && hasPermission(session.data, "Import");

  const libraries = useQuery({
    queryKey: ["libraries"],
    queryFn: () => unwrap(api.GET("/api/libraries")),
  });
  const monthEnd = useMemo(() => {
    const d = new Date(month);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
  }, [month]);
  const cases = useQuery({
    queryKey: ["cases"],
    queryFn: () => unwrap(api.GET("/api/cases")),
  });
  const filter = showFilter(library, reviewOnly);
  const days = useQuery({
    queryKey: ["timeline", "days", month, library, reviewOnly],
    queryFn: () =>
      unwrap(
        api.GET("/api/timeline/days", {
          params: { query: { from: iso(month), to: iso(monthEnd), ...filter } },
        }),
      ),
  });
  // Until a day is chosen, show the latest day with recordings this month.
  const shown =
    range ??
    (() => {
      const last = days.data?.days.at(-1);
      if (!last) return null;
      const start = Date.parse(`${last.date}T00:00:00Z`);
      return { from: start, to: start + DAY };
    })();
  const { timeline, events } = useLanesAndEvents(shown, library, reviewOnly);

  const goTo = () => {
    const t = Date.parse(`${jump.replace(" ", "T")}Z`);
    if (Number.isNaN(t)) return;
    const d = new Date(t);
    setMonth(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
    setRange({ from: t - HOUR, to: t + HOUR });
  };

  return (
    <Stack spacing={3} component="section">
      <Typography variant="h5" component="h2">
        Timeline
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          sx={{ gap: 2, flexWrap: "wrap", alignItems: "center" }}
        >
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel id="timeline-library" shrink>
              Show
            </InputLabel>
            <Select
              labelId="timeline-library"
              label="Show"
              displayEmpty
              notched
              value={library}
              onChange={(e) => setLibrary(e.target.value)}
            >
              <MenuItem value="">All I can view</MenuItem>
              {libraries.data?.map((l) => (
                <MenuItem key={l.library_id} value={`lib:${l.library_id}`}>
                  {l.team_name}
                </MenuItem>
              ))}
              {cases.data?.map((c) => (
                <MenuItem key={c.id} value={`case:${c.id}`}>
                  Case: {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={reviewOnly}
                onChange={(e) => setReviewOnly(e.target.checked)}
              />
            }
            label="Needs review only"
          />
          <Box sx={{ flexGrow: 1 }} />
          <TextField
            size="small"
            label="Go to (UTC)"
            placeholder="2026-10-09 14:45"
            value={jump}
            onChange={(e) => setJump(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && goTo()}
          />
          <Button variant="outlined" onClick={goTo}>
            Go
          </Button>
        </Stack>
      </Paper>

      {(days.data?.needs_review ?? 0) > 0 && (
        <Alert
          severity="warning"
          action={
            canResolve && (
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate("/needs-review")}
              >
                Review
              </Button>
            )
          }
        >
          {days.data?.needs_review} file
          {days.data?.needs_review === 1 ? " needs" : "s need"} review: no
          source identified, or a low-confidence start time.
        </Alert>
      )}
      {libraries.isSuccess &&
        libraries.data.length === 0 &&
        cases.data?.length === 0 && (
          <Alert severity="info">
            You can't view any team's or case's recordings yet. Ask an
            administrator.
          </Alert>
        )}

      <MonthCalendar
        month={month}
        days={days.data?.days ?? []}
        selected={shown}
        onMonth={(m) => {
          setMonth(m);
          setRange(null);
        }}
        onDay={(d) => setRange({ from: d, to: d + DAY })}
      />

      {area && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={() => setArea(null)}>
              Clear
            </Button>
          }
        >
          Showing recordings made while their unit was inside the area drawn on
          the map
          {inArea &&
            ` (${[...inArea.values()].reduce((n, l) => n + l.length, 0)} visits)`}
          .
        </Alert>
      )}

      <WordSearch
        filter={{ library: filter.library, case: filter.case }}
        onOpen={(t) => play(t, true, { from: t - HOUR, to: t + HOUR })}
      />

      {shown && (
        <Lanes
          range={shown}
          onBrush={setBrush}
          lanes={
            area && inArea
              ? filterLanes(timeline.data?.lanes ?? [], byUnit(inArea))
              : (timeline.data?.lanes ?? [])
          }
          events={events.data?.events ?? []}
          truncated={timeline.data?.truncated ?? false}
          loading={timeline.isPending}
          onRange={setRange}
          playhead={null}
          onSeek={play}
        />
      )}

      {shown && (
        <GpsAnalysis
          range={shown}
          filter={{ library: filter.library, case: filter.case }}
          onPlay={(t) => play(t, true)}
        />
      )}

      {shown && (
        <MapView
          lanes={timeline.data?.lanes ?? []}
          playhead={null}
          onSeek={play}
          span={brush ?? (shown.to - shown.from < DAY ? shown : null)}
          area={area}
          onArea={setArea}
          onAreaIntervals={setInArea}
        />
      )}
    </Stack>
  );
}

function MonthCalendar({
  month,
  days,
  selected,
  onMonth,
  onDay,
}: {
  month: number;
  days: { date: string; seconds: number; files: number }[];
  selected: Range | null;
  onMonth: (m: number) => void;
  onDay: (d: number) => void;
}) {
  const theme = useTheme();
  const d = new Date(month);
  const year = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const count = new Date(Date.UTC(year, m + 1, 0)).getUTCDate();
  const lead = (new Date(month).getUTCDay() + 6) % 7;
  const byDate = new Map(days.map((x) => [x.date, x]));
  const shades = [
    theme.palette.action.hover,
    alpha(theme.palette.primary.main, 0.2),
    alpha(theme.palette.primary.main, 0.45),
    alpha(theme.palette.primary.main, 0.75),
    theme.palette.primary.dark,
  ];
  const level = (h: number) =>
    h === 0 ? 0 : 1 + LEVELS.filter((l) => h >= l).length;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="Month"
    >
      <Stack direction="row" sx={{ alignItems: "center", gap: 1, mb: 1 }}>
        <IconButton
          aria-label="Previous month"
          onClick={() => onMonth(Date.UTC(year, m - 1, 1))}
        >
          ‹
        </IconButton>
        <Typography
          variant="h6"
          component="h3"
          sx={{ minWidth: 180, textAlign: "center" }}
        >
          {new Date(month).toLocaleString("en-GB", {
            month: "long",
            year: "numeric",
            timeZone: "UTC",
          })}
        </Typography>
        <IconButton
          aria-label="Next month"
          onClick={() => onMonth(Date.UTC(year, m + 1, 1))}
        >
          ›
        </IconButton>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 0.5,
        }}
      >
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((w) => (
          <Typography
            key={w}
            variant="caption"
            color="text.secondary"
            sx={{ px: 1 }}
          >
            {w}
          </Typography>
        ))}
        {Array.from({ length: lead }, (_, i) => (
          <span key={`blank${i}`} />
        ))}
        {Array.from({ length: count }, (_, i) => {
          const day = Date.UTC(year, m, i + 1);
          const date = iso(day).slice(0, 10);
          const info = byDate.get(date);
          const hours = (info?.seconds ?? 0) / 3600;
          const lv = level(hours);
          const isSelected = selected !== null && utcDay(selected.from) === day;
          return (
            <Button
              key={date}
              onClick={() => onDay(day)}
              aria-label={`${i + 1}: ${hours === 0 ? "no recordings" : `${hours.toFixed(1)} hours recorded`}${isSelected ? ", selected" : ""}`}
              aria-pressed={isSelected}
              sx={{
                minHeight: 52,
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexDirection: "column",
                textTransform: "none",
                bgcolor: shades[lv],
                color: lv >= 3 ? "primary.contrastText" : "text.primary",
                outline: isSelected
                  ? `3px solid ${theme.palette.secondary.main}`
                  : "none",
                outlineOffset: -3,
                "&:hover": { bgcolor: shades[lv], filter: "brightness(0.95)" },
              }}
            >
              <span>{i + 1}</span>
              <Typography variant="caption">
                {hours === 0
                  ? ""
                  : `${hours < 1 ? hours.toFixed(1) : Math.round(hours)} h`}
              </Typography>
            </Button>
          );
        })}
      </Box>
    </Paper>
  );
}

export function Lanes({
  range,
  lanes,
  events,
  truncated,
  loading,
  onRange,
  onBrush,
  playhead,
  onSeek,
  playing = false,
  onPause,
}: {
  range: Range;
  lanes: TimelineLane[];
  /** Speech and loud events (design §8.4). */
  events: TimelineEvent[];
  truncated: boolean;
  loading: boolean;
  onRange: (r: Range) => void;
  /** The span being dragged across the lanes; null when the drag ends. */
  onBrush: (r: Range | null) => void;
  playhead: number | null;
  onSeek: (utcMs: number, play?: boolean) => void;
  /** On the Playback page: the player is playing, and Play becomes Pause. */
  playing?: boolean;
  onPause?: () => void;
}) {
  const theme = useTheme();
  const area = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x0: number; lanes: DOMRect; grid: DOMRect } | null>(
    null,
  );
  const fingers = useRef(new Map<number, number>());
  const pinch = useRef<{
    start: Range;
    lanes: DOMRect;
    at: [number, number];
  } | null>(null);
  const touch = useRef(false);
  const hold = useRef<{ timer: number; x: number; y: number } | null>(null);
  // While two fingers move, the lanes follow them; the range (and its fetch)
  // changes when they lift.
  const [preview, setPreview] = useState<Range | null>(null);
  const [details, setDetails] = useState<{
    lines: string[];
    left: number;
    top: number;
  } | null>(null);
  const [width, setWidth] = useState(1000);
  const shown = lanes.length > 0;
  useEffect(() => {
    const el = grid.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => e && setWidth(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [shown]);
  const labelWidth = width < 640 ? 110 : 200;
  const coarse = useMediaQuery("(pointer: coarse)");
  const view = preview ?? range;
  const [band, setBand] = useState<{
    left: number;
    width: number;
    from: number;
    to: number;
  } | null>(null);
  const span = view.to - view.from;
  const pct = (t: number) => ((t - view.from) / span) * 100;
  const step = tickStep(span, Math.max(2, (width - labelWidth) / 64));
  const ticks: number[] = [];
  for (let t = Math.ceil(view.from / step) * step; t < view.to; t += step)
    ticks.push(t);

  const day = { from: utcDay(range.from), to: utcDay(range.from) + DAY };
  const atDay = range.from === day.from && range.to === day.to;
  const pan = (fraction: number) =>
    onRange({
      from: range.from + span * fraction,
      to: range.to + span * fraction,
    });
  const select = (x: number) => {
    const d = drag.current;
    if (!d || d.lanes.width === 0) return null;
    const clamp = (v: number) =>
      Math.min(Math.max(v, d.lanes.left), d.lanes.right);
    const [l, r] = [clamp(Math.min(d.x0, x)), clamp(Math.max(d.x0, x))];
    const at = (v: number) =>
      range.from + ((v - d.lanes.left) / d.lanes.width) * span;
    return {
      left: l - d.grid.left,
      width: r - l,
      from: at(l),
      to: at(r),
    };
  };
  const cancelDrag = () => {
    drag.current = null;
    setBand(null);
    onBrush(null);
  };
  const cancelHold = () => {
    if (hold.current) window.clearTimeout(hold.current.timer);
    hold.current = null;
  };
  /** What is under a finger held still, since touch has no hover. */
  const showDetails = (x: number, y: number) => {
    const box = grid.current?.getBoundingClientRect();
    const lines = document
      .elementsFromPoint(x, y)
      .map((el) => el.getAttribute("title"))
      .filter((t): t is string => !!t);
    if (box && lines.length > 0)
      setDetails({
        lines,
        left: Math.min(x - box.left, box.width - 260),
        top: y - box.top + 24,
      });
  };
  const onPointerDown = (e: PointerEvent) => {
    const lanes = area.current?.getBoundingClientRect();
    const box = grid.current?.getBoundingClientRect();
    touch.current = e.pointerType === "touch";
    setDetails(null);
    if (e.button !== 0 || !lanes || !box) return;
    // A first finger starts afresh: a lift can go unseen when the lanes
    // re-render under a pinch.
    if (e.isPrimary) {
      fingers.current.clear();
      pinch.current = null;
    }
    fingers.current.set(e.pointerId, e.clientX);
    (e.target as Element).setPointerCapture?.(e.pointerId);
    if (fingers.current.size === 2) {
      cancelDrag();
      cancelHold();
      const [a = 0, b = 0] = [...fingers.current.values()];
      pinch.current = { start: range, lanes, at: [a, b] };
      return;
    }
    if (fingers.current.size === 1)
      drag.current = { x0: e.clientX, lanes, grid: box };
    if (touch.current && fingers.current.size === 1) {
      const [x, y] = [e.clientX, e.clientY];
      hold.current = {
        x,
        y,
        timer: window.setTimeout(() => {
          hold.current = null;
          cancelDrag();
          showDetails(x, y);
        }, 600),
      };
    }
  };
  const onPointerMove = (e: PointerEvent) => {
    if (fingers.current.has(e.pointerId))
      fingers.current.set(e.pointerId, e.clientX);
    if (
      hold.current &&
      Math.hypot(e.clientX - hold.current.x, e.clientY - hold.current.y) > 8
    )
      cancelHold();
    const p = pinch.current;
    if (p && fingers.current.size === 2) {
      const [a = 0, b = 0] = [...fingers.current.values()];
      setPreview(pinched(p.start, p.lanes, p.at, [a, b]));
      return;
    }
    if (!drag.current) return;
    const b = select(e.clientX);
    setBand(b);
    onBrush(b && b.width >= 4 ? { from: b.from, to: b.to } : null);
  };
  const onPointerCancel = (e: PointerEvent) => {
    fingers.current.delete(e.pointerId);
    cancelHold();
    cancelDrag();
  };
  const onPointerUp = (e: PointerEvent) => {
    fingers.current.delete(e.pointerId);
    cancelHold();
    if (pinch.current) {
      // The first finger up ends the pinch; the other one doesn't start a drag.
      if (preview) onRange(preview);
      pinch.current = null;
      setPreview(null);
      return;
    }
    const chosen = select(e.clientX);
    cancelDrag();
    if (!chosen) return;
    if (chosen.width < 4) {
      onSeek(chosen.from);
      return;
    }
    const centre = (chosen.from + chosen.to) / 2;
    const half = Math.max((chosen.to - chosen.from) / 2, 30_000);
    onRange({ from: centre - half, to: centre + half });
  };
  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    // A held finger shows details instead (showDetails).
    if (touch.current) return;
    cancelDrag();
    onRange(day);
  };

  const colour = (f: TimelineFile) =>
    f.kind === "video"
      ? theme.palette.primary.main
      : f.kind === "audio"
        ? theme.palette.success.main
        : f.kind === "gps"
          ? theme.palette.text.primary
          : theme.palette.text.secondary;

  const teams = [...new Set(lanes.map((l) => l.team_name))];
  const laneEvents = byLane(events);
  const inLanes = events.filter((e) =>
    lanes.some(
      (l) =>
        laneKey(l.library_id, l.source) === laneKey(e.library_id, e.source),
    ),
  );
  const shownEvents = withWords(inLanes).filter(isOfInterest);
  const from = playhead ?? range.from;
  const next = nextEvent(shownEvents, from);
  const previous = previousEvent(shownEvents, from);
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="Lanes"
    >
      <Stack
        direction="row"
        sx={{ alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2 }}
      >
        <Typography variant="h6" component="h3" sx={{ flexGrow: 1 }}>
          {iso(range.from).slice(0, 10)} {hhmm(range.from)} –{" "}
          {iso(range.to - 1).slice(0, 10) !== iso(range.from).slice(0, 10)
            ? `${iso(range.to).slice(0, 10)} `
            : ""}
          {hhmm(range.to)} UTC
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={lanes.length === 0}
          onClick={() =>
            playing && onPause
              ? onPause()
              : onSeek(playhead ?? firstStart(lanes, range), true)
          }
        >
          {playing && onPause ? "Pause" : "Play"}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => pan(-0.5)}
          aria-label="Earlier"
        >
          ‹ Earlier
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={atDay}
          onClick={() => onRange(day)}
        >
          Whole day
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => pan(0.5)}
          aria-label="Later"
        >
          Later ›
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={!previous}
          onClick={() => previous && onSeek(Date.parse(previous.start_utc))}
          aria-label="Previous event"
          title={previous ? describe(previous) : undefined}
        >
          ‹ Event
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={!next}
          onClick={() => next && onSeek(Date.parse(next.start_utc))}
          aria-label="Next event"
          title={next ? describe(next) : undefined}
        >
          Event ›
        </Button>
      </Stack>
      {truncated && (
        <Alert severity="info">Too many files to show at once: zoom in.</Alert>
      )}
      {!loading && lanes.length === 0 && (
        <Typography color="text.secondary">
          Nothing recorded in this period.
        </Typography>
      )}
      {lanes.length > 0 && (
        <Box
          ref={grid}
          onContextMenu={onContextMenu}
          sx={{
            position: "relative",
            userSelect: "none",
            display: "grid",
            gridTemplateColumns: `${labelWidth}px minmax(0, 1fr)`,
            columnGap: 2,
            rowGap: 0.75,
          }}
        >
          <span />
          <Box sx={{ position: "relative", height: 20 }}>
            {ticks.map((t) => (
              <Typography
                key={t}
                variant="caption"
                color="text.secondary"
                sx={{
                  position: "absolute",
                  transform: "translateX(-50%)",
                  fontFamily: "monospace",
                }}
                style={{ left: `${pct(t)}%` }}
              >
                {step >= DAY ? iso(t).slice(5, 10) : hhmm(t)}
              </Typography>
            ))}
          </Box>
          {teams.map((team) => (
            <TeamLanes
              key={team}
              team={team}
              lanes={lanes.filter((l) => l.team_name === team)}
              events={laneEvents}
              pct={pct}
              colour={colour}
              warning={theme.palette.warning.main}
              area={area}
              dragHandlers={{
                onPointerDown,
                onPointerMove,
                onPointerUp,
                onPointerCancel,
              }}
            />
          ))}
          {playhead !== null && playhead >= view.from && playhead < view.to && (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                width: "2px",
                pointerEvents: "none",
                bgcolor: "error.main",
              }}
              style={{
                left: `calc(${labelWidth}px + ${theme.spacing(2)} + (100% - ${labelWidth}px - ${theme.spacing(2)}) * ${pct(playhead) / 100})`,
              }}
            />
          )}
          {details && (
            <Paper
              role="status"
              aria-label="Details"
              elevation={4}
              onClick={() => setDetails(null)}
              sx={{
                position: "absolute",
                zIndex: 2,
                width: 260,
                p: 1,
                fontSize: "body2.fontSize",
              }}
              style={{ left: Math.max(details.left, 0), top: details.top }}
            >
              {details.lines.map((l) => (
                <Typography key={l} variant="body2">
                  {l}
                </Typography>
              ))}
            </Paper>
          )}
          {band && (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                pointerEvents: "none",
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                borderLeft: `1px solid ${theme.palette.primary.main}`,
                borderRight: `1px solid ${theme.palette.primary.main}`,
              }}
              style={{ left: band.left, width: band.width }}
            >
              <Typography
                variant="caption"
                sx={{ fontFamily: "monospace", px: 0.5, whiteSpace: "nowrap" }}
              >
                {hhmm(band.from, true)}–{hhmm(band.to, true)}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {inLanes.length > 0 && (
        <EventsList events={shownEvents} all={inLanes} onSeek={onSeek} />
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 2 }}
      >
        {coarse
          ? "Tap the lanes to play from that time; drag across them, or pinch, to zoom; drag with two fingers to move; hold a finger on a recording for its details."
          : "Click the lanes to play from that time; drag across them to zoom; right-click for the whole day."}{" "}
        Outlined: needs review. On a lane: speech (amber band), motion (blue
        band), loud events (red marks) and the camera moving (grey line).
      </Typography>
    </Paper>
  );
}

/** The first recording in the range, or the range's start. */
function firstStart(lanes: TimelineLane[], range: Range): number {
  const starts = lanes.flatMap((l) =>
    l.files.map((f) => Math.max(Date.parse(f.start_utc), range.from)),
  );
  return starts.length > 0 ? Math.min(...starts) : range.from;
}

function TeamLanes({
  team,
  lanes,
  events,
  pct,
  colour,
  warning,
  area,
  dragHandlers,
}: {
  team: string;
  lanes: TimelineLane[];
  events: Map<string, TimelineEvent[]>;
  pct: (t: number) => number;
  colour: (f: TimelineFile) => string;
  warning: string;
  area: React.RefObject<HTMLDivElement | null>;
  dragHandlers: {
    onPointerDown: (e: PointerEvent) => void;
    onPointerMove: (e: PointerEvent) => void;
    onPointerUp: (e: PointerEvent) => void;
    onPointerCancel: (e: PointerEvent) => void;
  };
}) {
  return (
    <>
      <Typography sx={{ gridColumn: "1 / -1", fontWeight: 500, mt: 1 }}>
        {team}
      </Typography>
      {lanes.map((lane) => (
        <Lane
          key={lane.source ?? ""}
          lane={lane}
          events={events.get(laneKey(lane.library_id, lane.source)) ?? []}
          pct={pct}
          colour={colour}
          warning={warning}
          area={area}
          dragHandlers={dragHandlers}
        />
      ))}
    </>
  );
}

function Lane({
  lane,
  events,
  pct,
  colour,
  warning,
  area,
  dragHandlers,
}: {
  lane: TimelineLane;
  events: TimelineEvent[];
  pct: (t: number) => number;
  colour: (f: TimelineFile) => string;
  warning: string;
  area: React.RefObject<HTMLDivElement | null>;
  dragHandlers: {
    onPointerDown: (e: PointerEvent) => void;
    onPointerMove: (e: PointerEvent) => void;
    onPointerUp: (e: PointerEvent) => void;
    onPointerCancel: (e: PointerEvent) => void;
  };
}) {
  return (
    <>
      <Typography
        variant="body2"
        sx={{
          alignSelf: "center",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {lane.source ?? "Source not identified"}
      </Typography>
      <Box
        ref={area}
        role="list"
        aria-label={`${lane.source ?? "Unidentified"} recordings`}
        {...dragHandlers}
        sx={{
          position: "relative",
          height: 24,
          bgcolor: "action.hover",
          borderRadius: 1,
          overflow: "hidden",
          cursor: "crosshair",
          // Vertical swipes scroll the page; sideways drags and pinches are ours.
          touchAction: "pan-y",
          [COARSE]: { height: 40 },
        }}
      >
        {lane.files.map((f) => {
          const start = Date.parse(f.start_utc);
          const end = f.end_utc ? Date.parse(f.end_utc) : start;
          const left = Math.max(pct(start), 0);
          const right = Math.min(pct(end), 100);
          const label = `${f.original_name}: ${hhmm(start, true)}${f.end_utc ? `–${hhmm(end, true)}` : ", end unknown"} UTC${f.state === "needs_review" ? ", needs review" : ""}${f.clock_offset_ms ? `, clock corrected by ${signedSeconds(f.clock_offset_ms)}` : ""}`;
          return (
            <Box
              key={f.id}
              role="listitem"
              aria-label={label}
              title={label}
              sx={{
                position: "absolute",
                top: 3,
                bottom: 3,
                borderRadius: 0.5,
                boxSizing: "border-box",
              }}
              style={{
                left: `${left}%`,
                width: `max(3px, ${right - left}%)`,
                background:
                  f.state === "needs_review" ? "transparent" : colour(f),
                border:
                  f.state === "needs_review" ? `2px solid ${warning}` : "none",
              }}
            />
          );
        })}
        {events.map((e) => {
          const start = Date.parse(e.start_utc);
          const left = Math.max(pct(start), 0);
          const right = Math.min(pct(Date.parse(e.end_utc)), 100);
          const loud = e.kind === "loud";
          // Said is drawn as the speech it was found in.
          if (e.kind === "stationary" || e.kind === "said") return null;
          const look = {
            loud: { bgcolor: "error.main", top: 0, height: 9 },
            speech: { bgcolor: "warning.main", bottom: 0, height: 4 },
            motion: { bgcolor: "info.main", bottom: 0, height: 4 },
            camera_moving: { bgcolor: "text.disabled", top: 0, height: 3 },
            moving: { bgcolor: "success.main", bottom: 0, height: 3 },
          }[e.kind] ?? { bgcolor: "text.secondary", bottom: 0, height: 4 };
          return (
            <Box
              key={`${e.kind}${e.file_id}${e.start_utc}`}
              aria-hidden
              title={`${describe(e)} at ${hhmm(start, true)} UTC`}
              sx={{ position: "absolute", ...look }}
              style={{
                left: `${left}%`,
                width: loud ? "2px" : `max(2px, ${right - left}%)`,
              }}
            />
          );
        })}
      </Box>
    </>
  );
}
